import { ControlGroup } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSpinner from "components/Atoms/CustomSpinner"
import Note from "components/Atoms/Note"
import RichTextEditor from "components/Atoms/RichTextEditor"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUploadCrop from "components/Molecules/FileUploadCrop"
import FormController from "components/Molecules/FormController"
import MutationOptions from "components/Organisms/MutationOptions"
import BackButton from "components/Templates/BackButton"
import FileUploadDialog from "components/Templates/FileUploadDialog"
import VarietySelector from "components/Templates/selectors/VarietySelector"
import { TRANSPORTATION_TYPES } from "data/static.items"
import {
  ALL_DEPARTMENTS,
  HARVEST_GRADES,
  SELLABLE_ITEM_GROUPS,
  SI_UNITS,
  SKUS,
} from "graphql/selector.queries.gql"
import { ADD_UPDATE_SELLABLE_ITEM } from "graphql/sellable-item/sellable-item.mutations.gql"
import {
  SELLABLE_ITEMS_DETAIL,
  SELLABLE_ITEMS_LIST,
} from "graphql/sellable-item/sellable-item.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import useErrors from "hooks/useErrors"
import useRouteForm from "hooks/useRouteForm"
import { cloneDeep, isEmpty, pick, set } from "lodash-es"
import sellableItemModel from "models/sellable-item.model"
import React, { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppToaster, { ErrorToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import { removeTypename } from "utils/objectUtils"
import ExternalReferences from "./ExternalReferences"
import { useToggle } from "@mantine/hooks"
import CustomCard from "components/Atoms/CustomCard"

// import FileUploadCrop from "components/Molecules/FileUploadCrop";
const SellableItemRegistration = () => {
  const [isOpen, setOpen] = useToggle()
  const navigate = useNavigate()
  const { id } = useParams()
  const [isFileUploadOpen, setUpload] = useState(false)
  const [details, setDetails, { loading }] = useRouteForm(SELLABLE_ITEMS_DETAIL)

  const parsedVariables = useMemo(() => {
    return {
      ...pick(details, [
        "id",
        "name",
        "codename",
        "shortDescription",
        "longDescription",
      ]),
      metadata: removeTypename(details?.metadata),
      photos: details?.photos?.map(p => ({
        id: p?.id,
        url: p?.url,
        mediaType: p?.mediaType ?? "PHT",
      })),
      itemGroups: details?.itemGroups?.map(getId),
      primaryPhoto: isEmpty(details.primaryPhoto)
        ? undefined
        : {
            id: details.primaryPhoto?.id,
            url: details.primaryPhoto?.url ?? details.primaryPhoto,
            mediaType: details.primaryPhoto?.mediaType ?? "PHT",
          },
      sellableVarieties: details.sellableVarieties?.map(sv => ({
        id: sv?.id,
        name: `${details?.name} - ${sv?.variety?.name}`,
        gradeId: sv?.grade?.id,
        uomCount: sv?.uomCount ?? 0,
        uomTypeId: sv?.uomType?.id,
        skuCount: 1,
        skuTypeId: sv?.skuType?.id,
        varietyId: sv?.variety?.id,
      })),
      transportationType: details?.transportationType?.id,
      secondarySkuType: details?.secondarySkuType?.id,
      allowedDepartments: details?.allowedDepartments?.map(getId),
      currentStockPercentage: details?.currentStockPercentage,
    }
  }, [details])

  const [errors] = useErrors({
    state: parsedVariables,
    schema: sellableItemModel,
  })

  function handleChange(path, value) {
    const detailsCopy = cloneDeep(details)
    if (path.includes("variety")) {
      const pathArray = path
        .split(".")
        ?.map((i, idx) => (idx === 2 ? "grade" : i))
      set(detailsCopy, pathArray, null)
    }
    set(detailsCopy, path, value)
    setDetails(detailsCopy)
  }

  const disabled = useFormDisabled("sellable_item", parsedVariables)
  if (loading) return <CustomSpinner />

  return (
    <CustomCard className="vt-sellable-variety-registration">
      <ExternalReferences
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        data={details?.externalReferences}
        item={{ name: details?.name }}
      />
      <div className="flex items-baseline w-full">
        <BackButton className="self-center" />
        <div className="container mx-auto flex justify-between">
          <div />
          <h4> {details?.name || "NA"} </h4>
          {details?.externalReferences?.length ? (
            <CustomButton
              unstyled
              outlined
              className="ml-3 self-baseline"
              minimal
              icon="document-open"
              onClick={() => setOpen(true)}
            >
              External
            </CustomButton>
          ) : (
            <div />
          )}
        </div>
      </div>
      <hr className="mt-0 mb-6" />
      <div className="container">
        <FormController
          errors={errors?.details}
          source={details}
          onChange={handleChange}
          disabled={disabled}
          disableAll={true}
        >
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3">
              <StringInput big={false} path="name" label="Name" required />
            </div>
            <div className="col-span-3">
              <StringInput
                big={false}
                path="codename"
                label="Codename"
                required
              />
            </div>
            <div className="col-span-3">
              <StringInput
                required
                big={false}
                label="Short Description"
                path="shortDescription"
              />
            </div>

            <div className="col-span-3">
              <FileUploadCrop
                required
                value={details?.primaryPhoto?.url ?? details?.primaryPhoto}
                label="Primary Photo"
                path="primaryPhoto"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-6">
              <DynamicMultiSelector
                fill
                query={SELLABLE_ITEM_GROUPS}
                path="itemGroups"
                label="Item Groups"
              />
            </div>
            <div className="col-span-3">
              <StaticSelector
                label="Transportation Type"
                items={TRANSPORTATION_TYPES}
                path="transportationType"
                renderText={item => {
                  return typeof item === "string"
                    ? TRANSPORTATION_TYPES.find(f => f.id === item)?.name
                    : item?.name ?? "-- Select --"
                }}
              />
            </div>
            <div className="col-span-3">
              <CustomFormGroup
                label="Additional Photos"
                labelInfo={
                  <span className="vt-highlight vt-dark">
                    ({details?.photos?.length || "No"} Photos uploaded)
                  </span>
                }
              >
                <CustomButton
                  intent="primary"
                  fill
                  icon="cloud-upload"
                  text="Upload Photos"
                  onClick={() => setUpload(true)}
                />
              </CustomFormGroup>
              <FileUploadDialog
                disabled={disabled}
                path="photos"
                isOpen={isFileUploadOpen}
                onClose={() => setUpload(false)}
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3">
              <StringInput
                big={false}
                label="HSN Code"
                path="metadata.hsnCode"
              />
            </div>
            <div className="col-span-3">
              <StringInput
                big={false}
                path="metadata.fssaiLicense"
                label="FSSAI License"
              />
            </div>
            <div className="col-span-3">
              <StringInput
                big={false}
                path="metadata.eanCode"
                label="EAN Code"
              />
            </div>
            <div className="col-span-3">
              <DynamicSelector
                required
                path="secondarySkuType"
                query={SKUS}
                label="Secondary SKU"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-6">
              <DynamicMultiSelector
                big={false}
                fill
                query={ALL_DEPARTMENTS}
                label="Departments"
                path="allowedDepartments"
                renderTag={item => item?.name}
                renderText={item => item?.name}
                searchBy={["name"]}
              />
            </div>
            <div className="col-span-6">
              <CustomFormGroup
                helperText="A variant quantity will be calculated by capacity of secondary SKU.
"
                label="Is Variant"
              >
                <CustomCheckbox path="metadata.isVariant" />
              </CustomFormGroup>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <CustomFormGroup fill label="Long Description">
                <RichTextEditor
                  value={details?.longDescription}
                  onEditorChange={value =>
                    handleChange("longDescription", value)
                  }
                />
              </CustomFormGroup>
            </div>
          </div>
          <h6 className="mb-1 mt-6">Sellable Crops</h6>
          <hr className="mt-0" />

          {details?.sellableVarieties?.length > 0 && (
            <div className="grid grid-cols-12 gap-4 mb-2 header">
              <div className="col-span-3">Crop</div>
              <div className="col-span-2">Grade</div>
              <div className="col-span-3">UOM</div>
              <div className="col-span-3">Sku Type</div>
              <div className="col-span-1"></div>
            </div>
          )}
          <section>
            {details?.sellableVarieties?.map?.((sv, idx) => {
              const path = `sellableVarieties.${idx}.`
              return (
                <div className="grid grid-cols-12 gap-4" key={sv?.id}>
                  <div className="col-span-3 ">
                    <VarietySelector path={path + "variety"} />
                  </div>
                  <div className="col-span-2 ">
                    <DynamicSelector
                      query={HARVEST_GRADES}
                      variables={{
                        id: sv?.variety?.id,
                      }}
                      path={path + "grade"}
                    />
                  </div>
                  <div className="col-span-3 ">
                    <ControlGroup fill>
                      <StringInput
                        type="number"
                        big={false}
                        path={path + "uomCount"}
                      />
                      <DynamicSelector
                        query={SI_UNITS}
                        path={path + "uomType"}
                      />
                    </ControlGroup>
                  </div>
                  <div className="col-span-3 ">
                    <DynamicSelector query={SKUS} path={path + "skuType"} />
                  </div>
                  <div className="col-span-1 flex items-start">
                    <CustomButton
                      icon="trash"
                      minimal
                      intent="danger"
                      onClick={() => {
                        setDetails(prev => ({
                          ...prev,
                          sellableVarieties: prev.sellableVarieties?.filter(
                            (_, i) => i !== idx
                          ),
                        }))
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </section>
          <h6 className="mb-1 mt-6">Preferred stock broadcast</h6>
          <hr className="mt-0" />
          <div className="grid grid-cols-4 gap-4">
            <StringInput
              big={false}
              type="number"
              path="currentStockPercentage"
              label="Stock Broadcast Percentage"
              min={0}
              max={100}
            />
            <Note intent="warning" style={{ gridColumn: "2/4" }}>
              <span className="font-700">Note: </span> This percent of ground
              stock will be visible in <b>every stock</b> of this sellable item
              for online bookings and this value will{" "}
              <b>override the DC Current Stock percentage</b>
            </Note>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3">
              <CustomButton
                disabled={disabled}
                icon="plus"
                intent="primary"
                text="Add Crop"
                onClick={() => {
                  setDetails(prev => ({
                    ...prev,
                    sellableVarieties: prev?.sellableVarieties?.concat?.({
                      grade: null,
                      variety: null,
                    }) ?? [
                      {
                        grade: null,
                        variety: null,
                      },
                    ],
                  }))
                }}
              />
            </div>
          </div>
          <hr className="my-0 mt-5" />
          <div className="flex justify-end mt-3">
            <MutationOptions
              disabled={disabled}
              refetchQueries={[
                {
                  query: SELLABLE_ITEMS_LIST,
                  variables: {
                    first: 13,
                    skip: 0,
                  },
                },
              ]}
              variables={parsedVariables}
              mutation={ADD_UPDATE_SELLABLE_ITEM}
              validate={state => {
                if (errors?.latest) {
                  AppToaster.show({
                    message: errors?.latest,
                    intent: "danger",
                    icon: "cross",
                  })
                  return false
                }

                if (!state.primaryPhoto?.url) {
                  return ErrorToast("Please upload a primary photo")
                }
                return true
              }}
              onSave={({ addUpdateSellableItem: ausi }) => {
                const si = ausi.sellableItem

                AppToaster.show({
                  message: `${si?.name} Registered successfully`,
                  intent: "success",
                  icon: "tick",
                })
                navigate(-1)
              }}
              onSaveAndAdd={({ addUpdateSellableItem: ausi }) => {
                const si = ausi.sellableItem

                AppToaster.show({
                  message: `${si?.name} Registered successfully`,
                  intent: "success",
                  icon: "tick",
                })
                navigate("/d/registration/sellable-items/new")
                setDetails({})
              }}
              onSaveAndEdit={({ addUpdateSellableItem: ausi }) => {
                const si = ausi.sellableItem
                AppToaster.show({
                  message: `${si?.name} Saved successfully`,
                  intent: "success",
                  icon: "tick",
                })
                if (!parseInt(id))
                  navigate(`/d/registration/sellable-items/${si?.id}`, {
                    replace: true,
                  })
              }}
              onError={err => {
                AppToaster.show({
                  message: err.message,
                  intent: "danger",
                  icon: "cross",
                })
              }}
            />
          </div>
        </FormController>
      </div>
    </CustomCard>
  )
}

export default SellableItemRegistration
